import { createElement } from "react";
import { CircleDollarSign, HandCoins, Percent, Users } from "lucide-react";
import { format, parseISO } from "date-fns";
import type {
  OfferAnalyticsGroupApi,
  OfferAnalyticsSurfaceApi,
  OfferGroupSummaryResponse,
  OfferRowApi,
  RevenueTrendResponse,
  RoomUpgradeSummaryResponse,
  SurfaceTabResponse,
} from "@/types/analytics-offers";
import { at } from "../../utils/analyticsI18n";
import { formatAnalyticsCurrency } from "../shared/analyticsFormatting";
import type {
  OfferSection,
  OfferTableRow,
  OffersKpi,
  RevenueTrendPoint,
  UpgradeSegment,
} from "./types";

const GROUP_SECTION_TITLES: Record<
  Exclude<OfferAnalyticsGroupApi, "ROOM_UPGRADE">,
  string
> = {
  UPSELL: "Upsell Offers",
  SERVICE: "Service Offers",
  ACTIVITY: "Activities offers",
};

const GROUP_SECTION_IDS: Record<
  Exclude<OfferAnalyticsGroupApi, "ROOM_UPGRADE">,
  string
> = {
  UPSELL: "upsell",
  SERVICE: "service",
  ACTIVITY: "activities",
};

const PRE_CHECK_OUT_UPSELL_SECTION = {
  id: "upsell-step-5",
  title: "Upsell Offers: Step 5",
} as const;

const UPGRADE_SEGMENT_COLORS = {
  purchased: "#CAC19F",
  skipped: "#D4D4D4",
  clicked: "#826BFF",
} as const;

function formatCount(value: number): string {
  return value.toLocaleString("en-US");
}

function percentChange(current: number, previous: number): number {
  if (current === previous) {
    return 0;
  }

  if (previous === 0) {
    return current === 0 ? 0 : 100;
  }

  return ((current - previous) / previous) * 100;
}

function trendFromChange(change: number): OffersKpi["trend"] {
  if (change === 0) {
    return "neutral";
  }

  return change > 0 ? "positive" : "negative";
}

function buildKpiDelta(
  current: number,
  previous: number,
): Pick<OffersKpi, "delta" | "trend"> {
  const change = percentChange(current, previous);
  const sign = change > 0 ? "+" : "";
  const formatted = change.toLocaleString("en-US", {
    maximumFractionDigits: 1,
  });

  return {
    delta: `${sign}${formatted}%${at(
      "common.fromPreviousPeriod",
      " from previous period",
    )}`,
    trend: trendFromChange(change),
  };
}

function formatNullablePercent(value: number | null): string {
  if (value == null) {
    return "—";
  }
  return `${value}%`;
}

function formatCentsAsCurrency(value: number | null): string {
  if (value == null) {
    return "—";
  }

  return formatAnalyticsCurrency(value / 100, {
    maximumFractionDigits: 2,
    symbol: "€",
    symbolPosition: "suffix",
  });
}

function formatCatalogPrice(price: number | null): string {
  if (price == null) {
    return "—";
  }

  return formatAnalyticsCurrency(price, {
    maximumFractionDigits: 2,
    symbol: "€",
    symbolPosition: "suffix",
  });
}

function formatTrendDayLabel(dayKey: string): string {
  try {
    return format(parseISO(dayKey), "MMM d");
  } catch {
    return dayKey;
  }
}

function resolveOfferImageUrl(photoId: string | null): string | undefined {
  if (!photoId?.trim()) {
    return undefined;
  }

  if (/^https?:\/\//i.test(photoId)) {
    return photoId;
  }

  return undefined;
}

function mapOfferDisplayName(offer: OfferRowApi["offer"]): string {
  if (!offer.exists) {
    return at("offers.table.deletedOfferName", "Deleted offer");
  }

  return offer.name;
}

function mapOfferRow(row: OfferRowApi): OfferTableRow {
  return {
    id: row.offer.id,
    imageUrl: resolveOfferImageUrl(row.offer.photoId),
    serviceName: mapOfferDisplayName(row.offer),
    catalogPrice: formatCatalogPrice(row.offer.price),
    opened: formatCount(row.opened),
    ordered: formatCount(row.ordered),
    revenue: formatCentsAsCurrency(row.revenueCents),
    isDeleted: !row.offer.exists,
  };
}

function getGroupSectionConfig(
  surface: OfferAnalyticsSurfaceApi,
  offerGroup: Exclude<OfferAnalyticsGroupApi, "ROOM_UPGRADE">,
): { id: string; title: string } {
  if (surface === "PRE_CHECK_OUT" && offerGroup === "UPSELL") {
    return PRE_CHECK_OUT_UPSELL_SECTION;
  }

  return {
    id: GROUP_SECTION_IDS[offerGroup],
    title: GROUP_SECTION_TITLES[offerGroup],
  };
}

function mapGroupToSection(group: OfferGroupSummaryResponse): OfferSection {
  const offerGroup = group.offerGroup as Exclude<
    OfferAnalyticsGroupApi,
    "ROOM_UPGRADE"
  >;
  const sectionConfig = getGroupSectionConfig(group.surface, offerGroup);

  const sortedOffers = [...group.offers].sort(
    (left, right) => right.revenueCents - left.revenueCents,
  );

  return {
    id: sectionConfig.id,
    title: sectionConfig.title,
    surface: group.surface,
    offerGroup: group.offerGroup,
    metrics: [
      {
        title: "Total guests contacted",
        value: formatCount(group.contacted),
      },
      { title: "Only Opened", value: formatCount(group.onlyOpened) },
      { title: "Guests offered", value: formatCount(group.offered) },
      { title: "Skipped", value: formatCount(group.skipped) },
    ],
    rows: sortedOffers.map(mapOfferRow),
  };
}

export function mapRoomUpgradeToKpis(
  summary: RoomUpgradeSummaryResponse | undefined,
): OffersKpi[] {
  if (!summary) {
    return [];
  }

  return [
    {
      title: "Total Revenue",
      value: formatCentsAsCurrency(summary.totalRevenueCents),
      icon: createElement(CircleDollarSign),
    },
    {
      title: "Conversion rate",
      value: formatNullablePercent(summary.conversionRate),
      icon: createElement(Percent),
    },
    {
      title: "Guests offered",
      value: formatCount(summary.guestsOffered),
      icon: createElement(Users),
    },
    {
      title: "Avg. Revenue",
      value: formatCentsAsCurrency(summary.avgRevenueCents),
      icon: createElement(HandCoins),
    },
  ];
}

export function mapRoomUpgradeToKpisWithComparison(
  current: RoomUpgradeSummaryResponse | undefined,
  previous: RoomUpgradeSummaryResponse | undefined,
  includeDeltas: boolean,
): OffersKpi[] {
  const currentKpis = mapRoomUpgradeToKpis(current);
  if (!includeDeltas) {
    return currentKpis;
  }

  const comparisons = [
    {
      current: current?.totalRevenueCents ?? 0,
      previous: previous?.totalRevenueCents ?? 0,
    },
    {
      current: current?.conversionRate ?? 0,
      previous: previous?.conversionRate ?? 0,
    },
    {
      current: current?.guestsOffered ?? 0,
      previous: previous?.guestsOffered ?? 0,
    },
    {
      current: current?.avgRevenueCents ?? 0,
      previous: previous?.avgRevenueCents ?? 0,
    },
  ];

  return currentKpis.map((metric, index) => ({
    ...metric,
    ...buildKpiDelta(comparisons[index].current, comparisons[index].previous),
  }));
}

function toSegmentPercent(value: number, total: number): number {
  if (total <= 0) {
    return 0;
  }
  return Math.round((value / total) * 100);
}

export function mapRoomUpgradeUsageToSegments(
  summary: RoomUpgradeSummaryResponse | undefined,
): UpgradeSegment[] {
  if (!summary) {
    return [];
  }

  const { skipped, clicked, purchased } = summary.usage;
  const total = skipped + clicked + purchased;

  return [
    {
      key: "purchased",
      name: at("offers.roomUpgrade.segments.purchased", "Purchased"),
      value: toSegmentPercent(purchased, total),
      fill: UPGRADE_SEGMENT_COLORS.purchased,
    },
    {
      key: "skipped",
      name: at("offers.roomUpgrade.segments.skipped", "Skipped"),
      value: toSegmentPercent(skipped, total),
      fill: UPGRADE_SEGMENT_COLORS.skipped,
    },
    {
      key: "clicked",
      name: at("offers.roomUpgrade.segments.clickedOnly", "Clicked only"),
      value: toSegmentPercent(clicked, total),
      fill: UPGRADE_SEGMENT_COLORS.clicked,
    },
  ];
}

export function mapRevenueTrendToChartPoints(
  response: RevenueTrendResponse | undefined,
): RevenueTrendPoint[] {
  if (!response) {
    return [];
  }

  return response.series.map((point) => ({
    day: formatTrendDayLabel(point.day),
    revenue: point.revenueCents / 100,
  }));
}

export function mapSurfaceTabToSections(
  response: SurfaceTabResponse | undefined,
): OfferSection[] {
  if (!response) {
    return [];
  }

  return response.groups.map(mapGroupToSection);
}
